import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SupportLayout } from './SupportLayout';

interface QA { q: string; a: React.ReactNode }

const SECTIONS: { title: string; items: QA[] }[] = [
  {
    title: 'Booking',
    items: [
      {
        q: 'How do I book a place?',
        a: 'Pick the dates on the listing, hit Reserve, and pay. If the host runs instant booking, your stay is locked in immediately. If not, the host has 24 hours to accept; you only get charged after they do.',
      },
      {
        q: 'When does my card get charged?',
        a: 'For instant bookings, at checkout. For requested bookings, only after the host accepts. We hold the money until check-in day, then release it to the host.',
      },
      {
        q: 'Can I message a host before booking?',
        a: <>Yes. Open any listing and use <strong>Contact host</strong>. Replies usually come within a few hours.</>,
      },
      {
        q: 'What if the place is wrong on arrival?',
        a: <>Call us within 24 hours of check-in (see <Link to="/contact" className="text-primary-600 underline">Contact</Link>). We'll help you get a refund or find another place.</>,
      },
    ],
  },
  {
    title: 'Payment',
    items: [
      {
        q: 'Which payment methods work?',
        a: 'Visa, Mastercard, and Amex through Stripe. Whish Money for hosts and guests inside Lebanon.',
      },
      {
        q: 'Are there hidden fees?',
        a: 'No. The total you see at checkout is what you pay. Service fee (if any) is shown on the line above the total.',
      },
      {
        q: 'Can I get a refund?',
        a: <>Refunds depend on the host's cancellation policy. See <Link to="/cancellation" className="text-primary-600 underline">Cancellation options</Link>.</>,
      },
    ],
  },
  {
    title: 'Hosting',
    items: [
      {
        q: 'How do I list my place?',
        a: <>Open <Link to="/become-host" className="text-primary-600 underline">Become a host</Link>, fill in the details, and we'll come visit. About 20 minutes on your end.</>,
      },
      {
        q: 'When do I get paid?',
        a: '24 hours after the guest checks in. Whish or bank transfer.',
      },
      {
        q: 'What if a guest damages something?',
        a: <>Send us photos and a description. Cover goes up to $1,000,000 per stay. Details on <Link to="/safety" className="text-primary-600 underline">Safety</Link>.</>,
      },
    ],
  },
];

export const HelpPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const filtered = query.trim()
    ? SECTIONS.map((s) => ({
        ...s,
        items: s.items.filter((it) =>
          (it.q + ' ' + (typeof it.a === 'string' ? it.a : '')).toLowerCase().includes(query.toLowerCase())
        ),
      })).filter((s) => s.items.length > 0)
    : SECTIONS;

  return (
    <SupportLayout
      title="Help center"
      subtitle="Answers to the things people actually ask. Can't find it? Email us."
    >
      <input
        type="search"
        placeholder="Search help…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-10"
      />

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Nothing matches "{query}". <Link to="/contact" className="text-primary-600 underline">Ask a human</Link>.
        </div>
      )}

      {filtered.map((section) => (
        <section key={section.title} className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">{section.title}</h2>
          <div className="space-y-4">
            {section.items.map((it, i) => (
              <Item key={i} qa={it} />
            ))}
          </div>
        </section>
      ))}

      <div className="mt-12 p-6 bg-primary-50 rounded-2xl text-center">
        <p className="text-gray-900 font-medium mb-2">Still stuck?</p>
        <p className="text-gray-600 mb-4">A real person on the Beirut team can help.</p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
        >
          Contact us
        </Link>
      </div>
    </SupportLayout>
  );
};

const Item: React.FC<{ qa: QA }> = ({ qa }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="font-medium text-gray-900">{qa.q}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-5 pb-5 text-gray-600">{qa.a}</div>}
    </div>
  );
};

export default HelpPage;
